import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from './AuthService';
import { restClient } from './RestClient';
import { AdminUser } from './Model';

interface SuperUserDashboardProps {
  onError: (message: string, type?: 'error' | 'warning' | 'info') => void;
}

export const SuperUserDashboard: React.FC<SuperUserDashboardProps> = ({ onError }) => {
  const navigate = useNavigate();
  const isSuperUser = AuthService.isSuperUser();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);

  useEffect(() => {
    if (!isSuperUser) {
      onError('You do not have access to the admin dashboard.', 'error');
      navigate('/team-selection');
    }
  }, [isSuperUser, navigate, onError]);

  useEffect(() => {
    if (!isSuperUser) return;

    const fetchUsers = async () => {
      setLoading(true);
      const result = await restClient.getAdminUsers();
      if (result.IsSuccess) {
        setUsers(result.Value);
      } else {
        onError(result.Error.description || 'Failed to load users', 'error');
      }
      setLoading(false);
    };

    fetchUsers();
  }, [isSuperUser, onError]);

  const handleToggleAdmin = async (user: AdminUser) => {
    setTogglingUserId(user.id);
    const result = await restClient.updateUserAdminStatus(user.id, { isAdmin: !user.isAdmin });
    if (result.IsSuccess) {
      setUsers(prev => prev.map(u => u.id === user.id ? result.Value : u));
    } else {
      onError(result.Error.description || 'Failed to update admin status', 'error');
    }
    setTogglingUserId(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isSuperUser) {
    return null;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="page-description">
          Manage users and admin permissions.
        </p>
      </div>
      <div className="card">
        <div className="card-body">
          <h2 className="text-lg font-semibold mb-4">User Management</h2>
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <div className="spinner" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nickname</th>
                    <th>Active</th>
                    <th>Admin</th>
                    <th>Super User</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>{user.nickname}</td>
                      <td>
                        <span className={`badge ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {user.isSuperUser ? (
                          <span className={`badge ${user.isAdmin ? 'badge-success' : 'badge-secondary'}`}>
                            {user.isAdmin ? 'Admin' : 'Not Admin'}
                          </span>
                        ) : (
                          <button
                            className={`btn btn-sm ${user.isAdmin ? 'btn-success' : 'btn-outline-secondary'}`}
                            onClick={() => handleToggleAdmin(user)}
                            disabled={togglingUserId === user.id}
                          >
                            {togglingUserId === user.id ? 'Updating...' : user.isAdmin ? 'Admin' : 'Grant Admin'}
                          </button>
                        )}
                      </td>
                      <td>
                        {user.isSuperUser && (
                          <span className="badge badge-accent">Super User</span>
                        )}
                      </td>
                      <td className="text-muted text-sm">{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
